import { Link, useLoaderData } from 'react-router';
import { api } from '@/lib/api';
import { fmtAgo } from '@/lib/format';
import { RowLink } from '@/components/row-link';

type Flow = { id: string; name: string; status: string; trigger: { type: string; days?: number; segmentId?: string; tag?: string }; steps: unknown[]; stats: Record<string, number>; updatedAt: string };
const STATUS: Record<string, string> = { draft: 'bg-slate-100 text-slate-700', active: 'bg-emerald-100 text-emerald-800', paused: 'bg-amber-100 text-amber-900', archived: 'bg-slate-100 text-slate-500' };

export async function flowsLoader() {
  return api<Flow[]>('/flows');
}

export function FlowsPage() {
  const flows = useLoaderData<typeof flowsLoader>();
  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl">Flows</h1>
          <p className="text-sm text-muted-foreground">Trigger → steps. Waits are durable (Cloud Tasks in prod, a 1-minute poller locally). Starter flows are seeded as drafts.</p>
        </div>
        <Link to="/flows/new" className="rounded-md bg-[var(--ff-primary)] px-4 py-2 text-sm text-white">
          New flow
        </Link>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="data-table">
          <thead>
            <tr>
              <th>Flow</th>
              <th>Status</th>
              <th>Trigger</th>
              <th>Steps</th>
              <th>Runs (active / done)</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {flows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-muted-foreground">
                  No flows yet.
                </td>
              </tr>
            )}
            {flows.map((f) => (
              <RowLink key={f.id} href={`/flows/${f.id}`}>
                <td>
                  <Link to={`/flows/${f.id}`} className="ff-link font-medium">
                    {f.name}
                  </Link>
                </td>
                <td>
                  <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS[f.status] ?? ''}`}>{f.status}</span>
                </td>
                <td className="text-xs">
                  {f.trigger.type}
                  {f.trigger.days ? ` ${f.trigger.days}d` : ''}
                  {f.trigger.tag ? ` #${f.trigger.tag}` : ''}
                </td>
                <td>{f.steps.length}</td>
                <td className="text-xs">
                  {(f.stats.running ?? 0) + (f.stats.waiting ?? 0) + (f.stats.paused ?? 0)} / {(f.stats.completed ?? 0) + (f.stats.exited ?? 0)}
                  {f.stats.failed ? <span className="ml-1 text-red-700">{f.stats.failed} failed</span> : null}
                </td>
                <td className="whitespace-nowrap text-muted-foreground">{fmtAgo(f.updatedAt)}</td>
              </RowLink>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
